import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  ArrowDown,
  ArrowRight,
  ArrowUp,
  ArrowUpRight,
  Check,
  CirclePercent,
  Minus,
  Percent,
} from 'lucide-react'
import {
  getMetricStatus,
  getTrendInfo,
  getMetricInfo
} from '../utils/performanceThresholds'
import { formatCurrency, formatPercentage } from '../utils/financialCalculations'

const KPI_ICONS_BY_TYPE = {
  conversionRate: ArrowRight,
  revenue: ArrowUpRight,
  revenueGrowth: ArrowUpRight,
  expenseRatio: CirclePercent,
  netProfit: ArrowUpRight,
  profitMargin: Percent
}

const TREND_ICONS_BY_DIRECTION = {
  up: ArrowUp,
  down: ArrowDown,
  stable: Minus,
  neutral: ArrowRight
}

const KPICard = React.memo(({
  metricType,
  title,
  value,
  previousValue,
  icon,
  isPercentage = false,
  isCurrency = false,
  comparisonLabel = null,
  comparisonOptions = [],
  selectedComparisonKey = null,
  onComparisonMonthSelect = null
}) => {
  const [showTooltip, setShowTooltip] = useState(false)
  const [isComparisonSelectorOpen, setIsComparisonSelectorOpen] = useState(false)
  const comparisonSelectorRef = useRef(null)
  const { t, i18n } = useTranslation('dashboard')
  
  // Memoize expensive calculations
  const computedData = useMemo(() => {
    const status = getMetricStatus(metricType, value, previousValue)
    const trendInfo = getTrendInfo(value, previousValue)
    const metricInfo = getMetricInfo(metricType)
    
    return { status, trendInfo, metricInfo }
  }, [metricType, value, previousValue])

  const { status, trendInfo, metricInfo } = computedData

  const formatValue = (val) => {
    if (val === null || val === undefined) return '—'
    if (isCurrency) return formatCurrency(val)
    if (isPercentage) return formatPercentage(val)
    return val.toLocaleString()
  }

  const getTrendColor = (direction, isSmallChange = false) => {
    if (direction === 'neutral') return 'text-[#98A2B3]'
    if (direction === 'stable' || isSmallChange) return 'text-[#667085]'

    const isFavorable = metricType === 'expenseRatio'
      ? direction === 'down'
      : direction === 'up'
    return isFavorable ? 'text-[#147A4A]' : 'text-[#D92D20]'
  }

  const MetricIcon = typeof icon === 'function' ? icon : KPI_ICONS_BY_TYPE[metricType]
  const hasPreviousValue = value !== null && value !== undefined && previousValue !== null && previousValue !== undefined
  const numericDelta = hasPreviousValue ? Number(value) - Number(previousValue) : null
  const displayTrendDirection = hasPreviousValue
    ? numericDelta > 0 ? 'up' : numericDelta < 0 ? 'down' : 'stable'
    : metricType === 'revenueGrowth' && Number(value) !== 0
      ? Number(value) > 0 ? 'up' : 'down'
      : 'neutral'
  const isSmallChange =
    hasPreviousValue && previousValue !== 0 && trendInfo.direction === 'stable' && numericDelta !== 0
  const isPercentagePointMetric = metricType === 'profitMargin' || metricType === 'expenseRatio'

  const TrendIcon = TREND_ICONS_BY_DIRECTION[displayTrendDirection] || ArrowRight
  const formattedPointChange = hasPreviousValue
    ? new Intl.NumberFormat(i18n.language, { minimumFractionDigits: 1, maximumFractionDigits: 1 })
      .format(Math.abs(numericDelta))
    : null
  const displayTrendText = isPercentagePointMetric && hasPreviousValue
    ? `${formattedPointChange} ${t('kpi.percentagePoints')}`
    : metricType === 'revenueGrowth' && trendInfo.change === null
      ? formatPercentage(value ?? 0)
      : trendInfo.changeText
  const isExactZeroChange = hasPreviousValue && Math.abs(numericDelta) < 1e-6
  const hideTrendIconForZeroChange = isExactZeroChange
  const resolvedTrendText = hideTrendIconForZeroChange ? `— ${t('kpi.same')}` : displayTrendText
  const trendColor = getTrendColor(displayTrendDirection, isSmallChange)

  const getStatusLabel = (status) => {
    const labels = {
      excellent: t('healthScore.status.excellent'),
      good: t('healthScore.status.good'),
      warning: t('healthScore.status.warning'),
      poor: t('healthScore.status.poor'),
      neutral: t('healthScore.status.neutral')
    }
    return labels[status] || t('healthScore.status.neutral')
  }

  const hasComparisonMonthSelector =
    comparisonOptions.length > 1 && typeof onComparisonMonthSelect === 'function'
  const activeComparisonKey = selectedComparisonKey || comparisonOptions[0]?.key || null

  useEffect(() => {
    if (!hasComparisonMonthSelector && isComparisonSelectorOpen) {
      setIsComparisonSelectorOpen(false)
    }
  }, [hasComparisonMonthSelector, isComparisonSelectorOpen])

  useEffect(() => {
    if (!isComparisonSelectorOpen) return undefined

    const onDocumentMouseDown = (event) => {
      if (
        comparisonSelectorRef.current &&
        !comparisonSelectorRef.current.contains(event.target)
      ) {
        setIsComparisonSelectorOpen(false)
      }
    }

    const onDocumentKeyDown = (event) => {
      if (event.key === 'Escape') {
        setIsComparisonSelectorOpen(false)
      }
    }

    document.addEventListener('mousedown', onDocumentMouseDown)
    document.addEventListener('keydown', onDocumentKeyDown)

    return () => {
      document.removeEventListener('mousedown', onDocumentMouseDown)
      document.removeEventListener('keydown', onDocumentKeyDown)
    }
  }, [isComparisonSelectorOpen])

  const resolvedComparisonLabel = comparisonLabel || t(`kpi.${metricType}.trend`)
  const resolvedComparisonText = resolvedComparisonLabel || t(`kpi.${metricType}.trend`)
  const isPositiveRevenue = metricType === 'revenue' && (status === 'excellent' || status === 'good')
  const cardSurfaceClasses = isPositiveRevenue
    ? 'border-[#BDE9D1] bg-[#EAF8F1]'
    : metricType === 'revenue'
      ? 'border-[#F3C5D1] bg-[#FFF5F7]'
      : 'border-[#E7E9EE] bg-white'
  const metricIconColor = isPositiveRevenue
    ? 'text-[#147A4A]'
    : metricType === 'revenue'
      ? 'text-[#E1174C]'
      : 'text-[#98A2B3]'

  return (
    <div className="relative h-full min-w-0">
      <div
        className={`group grid h-full min-h-[174px] grid-rows-[32px_30px_32px_16px] content-start gap-[12px] rounded-[13px] border p-[18px] transition-shadow duration-200 ${cardSurfaceClasses} hover:shadow-sm`}
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
      >
        <div className="flex items-center justify-between gap-3">
          <h3 className="min-w-0 text-xs font-semibold leading-4 text-[#667085]">{title}</h3>
          {MetricIcon ? (
            <MetricIcon
              className={`h-4 w-4 shrink-0 ${metricIconColor}`}
              aria-hidden="true"
            />
          ) : icon ? (
            <span className="shrink-0 text-base text-[#98A2B3]" aria-hidden="true">{icon}</span>
          ) : null}
        </div>

        <div className="text-[25px] font-extrabold leading-[30px] tracking-tight text-[#111827]">
          {formatValue(value)}
        </div>

        <div className="flex min-w-0 flex-col items-start justify-center">
          <span className={`inline-flex min-w-0 max-w-full items-center gap-1 text-xs font-bold leading-4 ${trendColor}`}>
            {!hideTrendIconForZeroChange && <TrendIcon className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />}
            <span className="truncate">{resolvedTrendText}</span>
          </span>

          {hasComparisonMonthSelector ? (
            <div className="relative min-w-0" ref={comparisonSelectorRef}>
              <button
                type="button"
                onClick={() => {
                  setShowTooltip(false)
                  setIsComparisonSelectorOpen((prev) => !prev)
                }}
                className="w-full max-w-full truncate text-left text-[13px] leading-4 text-[#667085] hover:text-gray-800 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FF1B54]/40"
                aria-haspopup="listbox"
                aria-expanded={isComparisonSelectorOpen}
                aria-label={t('kpi.comparison.changeMonthAria')}
              >
                {resolvedComparisonText}
              </button>

              {isComparisonSelectorOpen && (
                <div className="absolute left-0 top-full z-40 mt-2 w-52 rounded-md border border-gray-200 bg-white shadow-lg">
                  <div className="border-b border-gray-100 px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                    {t('kpi.comparison.selectMonth')}
                  </div>
                  <div
                    role="listbox"
                    aria-label={t('kpi.comparison.selectMonth')}
                    className="max-h-56 overflow-y-auto py-1"
                  >
                    {comparisonOptions.map((option) => {
                      const isSelected = option.key === activeComparisonKey

                      return (
                        <button
                          key={option.key}
                          type="button"
                          role="option"
                          aria-selected={isSelected}
                          onClick={() => {
                            onComparisonMonthSelect(option.key)
                            setIsComparisonSelectorOpen(false)
                          }}
                          className={`flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-gray-50 ${
                            isSelected ? 'font-medium text-[#FF1B54]' : 'text-gray-700'
                          }`}
                        >
                          <span>{option.label}</span>
                          {isSelected && <Check className="h-4 w-4" aria-hidden="true" />}
                        </button>
                      )
                    })}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <span className="w-full max-w-full truncate text-[11px] leading-[14px] text-[#667085]">{resolvedComparisonText}</span>
          )}
        </div>

        <div className="truncate text-[11px] leading-[14px] text-[#667085]">
          {getStatusLabel(status)}
        </div>
      </div>

      {/* Tooltip */}
      {showTooltip && !isComparisonSelectorOpen && (
        <div className="absolute z-50 w-80 p-4 mt-2 bg-white rounded-lg shadow-lg border border-gray-200 left-0">
          <div className="mb-3">
            <h4 className="font-semibold text-gray-900 mb-1">{metricInfo.title}</h4>
            <p className="text-sm text-gray-600 mb-2">{metricInfo.description}</p>
            <p className="text-sm text-[#FF1B54] italic">{metricInfo.tip}</p>
          </div>
          
          <div className="mb-3">
            <h5 className="font-medium text-gray-800 text-xs mb-1">
              {t('performanceThresholds', { ns: 'common' }).toUpperCase()}
            </h5>
            <p className="text-xs text-gray-600">{metricInfo.thresholds}</p>
          </div>

          {hasPreviousValue && (
            <div className="pt-3 border-t border-gray-100">
              <h5 className="font-medium text-gray-800 text-xs mb-1">
                {t('monthOverMonth', { ns: 'common' }).toUpperCase()}
              </h5>
              <div className="flex items-center space-x-2">
                <span
                  className={`text-sm inline-flex items-center gap-1 ${trendColor}`}
                >
                  <TrendIcon className="w-4 h-4" aria-hidden="true" />
                  {resolvedTrendText}
                </span>
                <span className="text-xs text-gray-500">
                  ({formatValue(previousValue)} → {formatValue(value)})
                </span>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
})

// Add display name for debugging
KPICard.displayName = 'KPICard'

export default KPICard
